import Alpine from "alpinejs"
import { omit } from "lodash"
import validate, { validators, ERROR_MESSAGES } from "../validations"
import landingView from "./00-landing"
import weightView from "./03-weight"
import ageView from "./04-age"
import heightView from "./05-height"
import nameView from "./07-name"
import summaryEditView from "./08-summary-edit"
import idealWeightView from "./09-ideal-weight"
import planApetiteView from "./12-plan-apetite"
import planLikeView from "./13-plan-like"
import planFirstMealView from "./14-plan-first-meal"
import planFrequencyView from "./15-plan-frequency"
import createPlanView from "./16-create-plan"
import basicCheckoutView from "./18-basic-checkout"
import basicCheckoutMultipleOffersView from "./18-basic-checkout-multiple-offers"
import basicCheckoutAbKbView from "./18-basic-checkout-ab-kb"
import basicCheckoutAbKbMultipleOffersView from "./18-basic-checkout-ab-kb-multiple-offers"
import basicCcinputView from "./18-basic-ccinput"
import basicCcinputAbKbView from "./18-basic-ccinput-ab-kb"
import basicThankYouView from "./18-basic-thank-you"
import premiumOfferView from "./19-premium-offer"
import premiumPaymodesView from "./19-premium-paymodes"
import slOfferView from "./20-sl-offer"
import slPaymodesView from "./20-sl-paymodes"
import slAndPremiumOfferView from "./21-sl-and-premium-offer"
import slAndPremiumPaymodesView from "./21-sl-and-premium-paymodes"
import basicAnnualOfferView from "./22-basic-annual-offer"
import basicAnnualPaymodesView from "./22-basic-annual-paymodes"
import upsellsPopupView from "./upsellsPopup"

const INVALID_DOMAIN_CODE = "invalid_domain"
const EMAIL_VALIDATION_CODE = "email_verification_failed"

const view = (nextStepPath) => ({
  formValues: {},
  errors: {},

  loading: false,

  store: Alpine.store("data"),

  statusToTrack: null,

  listenExitIntent: true,

  init() {
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        this.store.reload()
        this.loading = false
        setTimeout(() => {
          this.initActions() // WARNING: initActions() called here!
        }, 1)
      }
    })

    setTimeout(() => {
      this.initActions()
    }, 1)
  },

  // initActions() seperated from init(),
  // because going back to previous view does not call init(),
  // but still keeps the eventListener.
  // Link to Jira issue: https://mitigate.atlassian.net/browse/NUS-262
  initActions() {
    this.formValues = Object.keys(this.formValues).reduce(
      (formValues, key) => ({
        ...formValues,
        [key]: this.store.values[key],
      }),
      {}
    )

    if (this.onInit) this.onInit()

    this.store.trackViewAndMaybeStatus(this.statusToTrack)

    requestAnimationFrame(this.focusField)
  },

  focusField() {
    const field = document.querySelector("[autofocus]")

    if (field) field.focus()
  },

  update(field, value) {
    this.formValues[field] = value
  },

  processFormValues() {
    return this.formValues
  },

  getNextStepPath(withToken = false) {
    if (!withToken || !this.store.values.token) return nextStepPath

    return `${nextStepPath}#stored-token=${this.store.values.token}`
  },

  scrollToFirstError() {
    document
      .querySelector("[data-error]")
      .scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })
  },

  get isValid() {
    return !Object.values(this.errors).some(Boolean)
  },

  validateField(field) {
    if (!this.submitted) return

    const formValues = this.processFormValues()

    const valid = validators[field](formValues[field], formValues)

    if (valid) {
      this.errors = omit(this.errors, field)
    } else {
      this.errors = {
        ...this.errors,
        [field]: ERROR_MESSAGES[field],
      }
    }
  },

  handleInternalRedirect(exitIntentHandlerPath) {
    if (
      !this.store.values.exitIntent &&
      exitIntentHandlerPath &&
      exitIntentHandlerPath.length > 1 &&
      this.listenExitIntent
    ) {
      window.location.replace(exitIntentHandlerPath)
    } else {
      history.back()
    }
  },

  handleApiError(e) {
    this.errors = { [e.path]: e.code }
  },

  isEmailValidationError() {
    const errors = this.errors
    if (errors) {
      if (errors.email === INVALID_DOMAIN_CODE || errors.email === EMAIL_VALIDATION_CODE) {
        return true
      }
    }

    return false
  },

  async submit(push = false, withToken = false) {
    this.submitted = true

    if (this.store.preloadPromise) {
      this.loading = true
      await this.store.preloadPromise
    }

    const formValues = this.processFormValues()

    const { hasErrors, errors } = validate(omit(formValues, this.skipValidationsFor || []))

    if (hasErrors) {
      if (errors.email) {
        this.formValues.email = null
        this.store.values.email = null
      }
      this.errors = errors
      requestAnimationFrame(this.scrollToFirstError)
    } else {
      this.loading = true
      this.store.values = { ...this.store.values, ...formValues }
      this.errors = {}

      if (!push || (await this.store.push(false, null, this.handleApiError.bind(this)))) {
        window.location = this.getNextStepPath(withToken)
      } else {
        const isEmailError = this.isEmailValidationError()
        if (isEmailError) {
          this.formValues.email = null
          this.store.values.email = null
        }
        this.loading = false
      }
    }
  },
})

export default [
  { name: "view", data: view },
  { name: "landingView", data: landingView(view) },
  { name: "weightView", data: weightView(view) },
  { name: "ageView", data: ageView(view) },
  { name: "heightView", data: heightView(view) },
  { name: "nameView", data: nameView(view) },
  { name: "summaryEditView", data: summaryEditView(view) },
  { name: "idealWeightView", data: idealWeightView(view) },
  { name: "planApetiteView", data: planApetiteView(view) },
  { name: "planLikeView", data: planLikeView(view) },
  { name: "planFirstMealView", data: planFirstMealView(view) },
  { name: "planFrequencyView", data: planFrequencyView(view) },
  { name: "createPlanView", data: createPlanView(view) },
  { name: "basicCheckoutView", data: basicCheckoutView(view) },
  { name: "basicCheckoutMultipleOffersView", data: basicCheckoutMultipleOffersView(view) },
  { name: "basicCheckoutAbKbView", data: basicCheckoutAbKbView(view) },
  { name: "basicCheckoutAbKbMultipleOffersView", data: basicCheckoutAbKbMultipleOffersView(view) },
  { name: "basicCcinputView", data: basicCcinputView(view) },
  { name: "basicCcinputAbKbView", data: basicCcinputAbKbView(view) },
  { name: "basicThankYouView", data: basicThankYouView(view) },
  { name: "premiumOfferView", data: premiumOfferView(view) },
  { name: "premiumPaymodesView", data: premiumPaymodesView(view) },
  { name: "slOfferView", data: slOfferView(view) },
  { name: "slPaymodesView", data: slPaymodesView(view) },
  { name: "slAndPremiumOfferView", data: slAndPremiumOfferView(view) },
  { name: "slAndPremiumPaymodesView", data: slAndPremiumPaymodesView(view) },
  { name: "basicAnnualOfferView", data: basicAnnualOfferView(view) },
  { name: "basicAnnualPaymodesView", data: basicAnnualPaymodesView(view) },
  { name: "upsellsPopupView", data: upsellsPopupView(view) },
]
